<template>
	<div id="grid-loader" class="vx-card vs-con-loading__container">
		<ag-grid-vue
			:gridOptions="gridOptions"
			class="ag-theme-balham w-100 ag-grid-table rebate-summary-table"
			:columnDefs="columnDefs"
			:defaultColDef="defaultColDef"
			:components="components"
			:rowData="autoRenewListData"
			rowSelection="multiple"
			:enableRangeSelection="true"
			:suppressCopyRowsToClipboard="true"
			:suppressDragLeaveHidesColumns="true"
			:animateRows="false"
			:floatingFilter="false"
			:pagination="false"
			:paginationPageSize="paginationPageSize"
			:suppressPaginationPanel="true"
			:suppressRowClickSelection="true"
			:rowClassRules="rowClassRules"
			:statusBar="statusBar"
			:sideBar="sideBar"
			:rowHeight="30"
			:headerHeight="30"
			:floatingFiltersHeight="30"
			:checkboxSelection="true"
			@filter-changed="onFilterChanged"
			@row-selected="onRowSelected"
			@pagination-changed="onPaginationChanged"
			@column-resized="onColumnResized" >
		</ag-grid-vue>
	</div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "ag-grid-enterprise";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import "@/assets/scss/radix/extraComponents/agGridStyleOverride.scss";

export default {
	name: 'AutoRenewList',
	components: {
		AgGridVue,
	},
	props: {
    autoRenewListData: {
      type: Array,
    },
    source: {
      type: String,
      default: null
    }
  },
	computed: {
    windowWidth: function () {
      return this.$store.state.windowWidth;
    },
    paginationPageSize() {
      if (this.gridApi) {
        return this.gridApi.paginationGetPageSize();
      } else {
        return 15;
      }
    },
    totalPages() {
      if (this.gridApi) {
        return this.gridApi.paginationGetTotalPages();
      } else {
        return 0;
      }
    },
    currentPage: {
      get() {
        if (this.gridApi) return this.gridApi.paginationGetCurrentPage() + 1;
        else return 1;
      },
      set(val) {
        this.gridApi.paginationGoToPage(val - 1);
      },
    },
  },
	watch: {
    windowWidth(val) {
      this.toggleColumnMovable(val);
      this.toggleColumnPinned(val);
    },
    gridApi(val) {
      if(val) {
        setTimeout(() => {
          let pinnedBottomData = this.generatePinnedBottomData();
          this.gridApi.setPinnedBottomRowData([pinnedBottomData]);
        }, 100);
      }
    },
    autoRenewListData(val) {
      if(val && val.length) {
        setTimeout(() => {
          let pinnedBottomData = this.generatePinnedBottomData();
          this.gridApi.setPinnedBottomRowData([pinnedBottomData]);
        }, 100);
      }
    }
  },
	data() {
		return {
			selectedRowCount: '',
			gridOptions: {},
			maxPageNumbers: 7,
			gridApi: null,
			defaultColDef: {
				sortable: true,
				resizable: true,
				suppressMenu: true,
				suppressMovable: false,
				filterParams: {
					suppressAndOrCondition: true,
					newRowsAction: 'keep',
				},
			},
			rowClassRules: null,
			statusBar: null,
			sideBar: null,
			columnDefs: null,
			tableData: null,
			globalSearchTerm: '',
			flags: {
				selectedAll: [],
			},
			components : {}
		}
	},
	beforeMount() {
		this.disableQueue();

    // Reset movable columns on mobile
    this.toggleColumnMovable(this.windowWidth);

    (this.columnDefs = this.source == 'clientPaymentDetails' || this.source == 'clientApproveSummary' ? [
      {
        headerName: "Type",
        field: "type",
        cellRenderer: (data) => {
          return this.toTitleCase(data.data.type);
        },
        minWidth: 90,
        width: 120,
      },
      {
        headerName: "Year",
        field: "year",
        minWidth: 100,
        width: 120,
      },
      {
        headerName: "Month",
        field: "month",
        minWidth: 130,
        width: 100,
      },
      {
        headerName: "Period",
        field: "period",
        minWidth: 90,
        width: 100,
      },
      {
        headerName: "Count",
        field: "count",
        minWidth: 90,
        width: 100,
      },
      {
        headerName: "Sum Rebate",
        field: "sum_rebate",
        minWidth: 150,
        width: 150,
      }
    ] 
    : 
    [
      {
        headerName: "Type",
        field: "type",
        cellRenderer: (data) => {
          return this.toTitleCase(data.data.type);
        },
        minWidth: 90,
        width: 120,
      },
      {
        headerName: "Year",
        field: "year",
        minWidth: 100,
        width: 120,
      },
      {
        headerName: "Month",
        field: "month",
        minWidth: 130,
        width: 100,
      },
      {
        headerName: "Promo Tag",
        field: "promo_tag",
        minWidth: 130,
        width: 150,
      },
      {
        headerName: "TLD",
        field: "tld",
        minWidth: 120,
        width: 100,
      },
      {
        headerName: "Period",
        field: "period",
        minWidth: 90,
        width: 100,
      },
      {
        headerName: "Net Revenue",
        field: "net_revenue",
        minWidth: 130,
        width: 150,
      },
      {
        headerName: "Count",
        field: "count",
        minWidth: 90,
        width: 100,
      },
      {
        headerName: "Sum Rebate",
        field: "sum_rebate",
        minWidth: 150,
        width: 150,
      },
      {
        headerName: "Sum Cost Adj",
        field: "sum_cost_adjustment",
        minWidth: 130,
        width: 150,
      },
      {
        headerName: "Sum Radix Rev.",
        field: "sum_radix_revenue",
        minWidth: 130,
        width: 150,
      },
    ]),
    // Set Grid sidebar
    (this.sideBar = {
			toolPanels: [
				{
					id: "columns",
					labelDefault: "Columns",
					labelKey: "columns",
					iconKey: "columns",
					toolPanel: "agColumnsToolPanel",
					toolPanelParams: {
						suppressRowGroups: true,
						suppressValues: true,
						suppressPivots: true,
						suppressPivotMode: true,
						suppressSideButtons: true,
						suppressColumnSelectAll: true,
						suppressColumnExpandAll: true,
					},
				},
			],
		});
	},
	mounted() {
		this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;

    // if (this.gridApi) {
    //   setTimeout(() => {
    //     let pinnedBottomData = this.generatePinnedBottomData();
    //     this.gridApi.setPinnedBottomRowData([pinnedBottomData]);
    //   }, 100);
    // }
    // Resize to fit columns
    this.sizeColumnsToFit();

    // Reset pinned columns on mobile
    this.toggleColumnPinned(this.windowWidth);
	},
	destroyed() {

	},
	methods: {
		onPageSizeChanged() {
      var value = document.getElementById("page-size").value;
      this.gridApi.paginationSetPageSize(Number(value));
    },
    onPaginationChanged() {
      if (this.gridApi) {
        (this.totalRows = this.gridApi.paginationGetRowCount()),
        (this.currentPageSize = this.gridApi.paginationGetPageSize());
      }
    },
    onRowSelected(event) {
      var getStatus = this.gridApi
        .getSelectedNodes()
        .map((x) => x.data)
        .map((x) => x.status);
      if (event.node.selected) {
        // Push to selectedPromos
        this.selectedRowCount = this.gridApi.getSelectedNodes().length;
        if (this.selectedRowCount > 30) {
          event.node.setSelected(false);

          this.$vs.notify({
            time: 5000,
            color: "warning",
            title: "Warning",
            text: "You may select only upto 30 rows",
          });
        }
      } else {
        this.selectedRowCount = this.gridApi.getSelectedNodes().length;
      }
    },
    onFilterChanged() {
      this.deselectAllSelectedRows();
    },
    openLoadingInDiv() {
      this.$vs.loading({
        container: "#div-with-loading",
        scale: 0.8,
      });
    },
    hideLoadingInDiv() {
      this.$vs.loading.close("#div-with-loading > .con-vs-loading");
    },
    toggleColumnMovable(val) {
      if (val <= 576) {
        this.defaultColDef["suppressMovable"] = true;
      } else {
        this.defaultColDef["suppressMovable"] = false;
      }
    },
    toggleColumnPinned(val) {
      if (val <= 576) {
        this.maxPageNumbers = 4;
        this.gridOptions.columnApi.setColumnPinned("type", null);
      } else {
        this.gridOptions.columnApi.setColumnPinned("type", "left");
      }
    },
    onColumnResized() {
      this.gridApi.resetRowHeights();
    },
    sizeColumnsToFit() {
      this.gridApi.sizeColumnsToFit();
    },
    disableQueue() {
      this.rowClassRules = {
        "ag-row-disabled": 'data && (data.status == "edit pending")',
        "ag-checkbox-disabled": 'data && (data.status == "edit pending")',
      };
    },
    // Refresh the page withou resetting filters
    refreshPage() {
      // this.initSelectedAllFlag();
      this.gridApi.deselectAll();
      this.gridApi.setSortModel(null);
    },
    // With filters for when we want to reset filters
    refreshPageWithFilters() {
      this.refreshPage();
      this.gridApi.setFilterModel(null);
    },
    gridSelectAll() {
      this.gridApi.forEachNodeAfterFilter((node) => {
				if (node["rowIndex"] >=	(this.currentPage - 1) * this.gridApi.paginationGetPageSize() && node["rowIndex"] <	this.currentPage * this.gridApi.paginationGetPageSize()) {
					node.setSelected(true);
				}
      });
    },
    gridDeselectAll() {
      this.gridApi.forEachNodeAfterFilter((node) => {
        if (node["rowIndex"] >=  (this.currentPage - 1) * this.gridApi.paginationGetPageSize() && node["rowIndex"] <  this.currentPage * this.gridApi.paginationGetPageSize()) {
          node.setSelected(false);
        }
      });
    },
		// Total Aggregate Data Row functions
    generatePinnedBottomData() {
      // generate a row-data with null values
      let result = {};

      this.gridColumnApi.getAllGridColumns().forEach((item) => {
        result[item.colId] = null;
      });

      return this.calculatePinnedBottomData(result);
    },
    calculatePinnedBottomData(target) {
      //**list of columns for aggregation**
      let columnsWithAggregation = [
        "net_revenue",
        "count",
        "sum_rebate",
        "sum_cost_adjustment",
        "sum_radix_revenue",
      ];
      columnsWithAggregation.forEach((element) => {
        this.gridApi.forEachNodeAfterFilter((rowNode) => {
          if (rowNode.data[element])
            target[element] += Number(rowNode.data[element]);
        });

        if (target[element]) target[element] = `${target[element].toFixed(2)}`;
      });
      return target;
    },
	}
}
</script>

<style>

</style>